import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Pencil, RotateCw, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/services";
import { MESSAGE_STATUS } from "@/lib/contact";
import { useAuth } from "@/context/AuthContext";
import { useServices } from "@/context/ServicesContext";
import { MessagingDialog } from "@/components/app/MessagingDialog";
import { MessageEditDialog } from "@/components/app/MessageEditDialog";
import { WhatsAppSettingsDialog } from "@/components/app/WhatsAppSettingsDialog";

const EDITABLE = ["preparada", "pendente", "falhou"];

const TemplatesCard = ({ onEdit, onWhatsApp, canEdit }) => {
  const { messaging } = useServices();
  if (!messaging) return null;
  const items = [["Lembrete de retorno", messaging.template, "tpl-reminder"], ["Aviso de agendamento", messaging.notice_template, "tpl-notice"], ["Confirmação de visita", messaging.visit_template, "tpl-visit"], ["Saudação", messaging.greeting_template, "tpl-greeting"]];
  return (
    <section className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4" data-testid="templates-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold">Modelos de mensagem</h2>
          <p className="text-xs text-slate-400">Envio automático: <b className={messaging.email_enabled ? "text-emerald-300" : "text-slate-300"} data-testid="templates-email-status">{messaging.email_enabled ? "ligado" : "desligado"}</b> · Canal: <b className="text-slate-200" data-testid="templates-channel">{messaging.channel === "whatsapp" ? "WhatsApp Business" : "E-mail"}</b></p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onWhatsApp} className="rounded-full border-[#25D366]/50 bg-transparent text-[#25D366]" data-testid="templates-whatsapp-button"><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp Business</Button>
            <Button variant="outline" onClick={onEdit} className="rounded-full border-slate-700 bg-transparent" data-testid="templates-edit-button"><Settings2 className="mr-1 h-4 w-4" /> Editar modelos</Button>
          </div>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {items.map(([label, text, id]) => (
          <div key={id} className="rounded-lg border border-slate-700/60 bg-slate-950/50 p-3" data-testid={id}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
            <p className="mt-1 text-sm text-slate-200">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const PAGE = 30;
const ALL = "todos";

const Row = ({ m, onRetry, onEdit }) => {
  const [label, cls] = MESSAGE_STATUS[m.status] || [m.status, "text-slate-300"];
  return (
    <li className="rounded-xl border border-slate-700/60 bg-slate-800/60 p-3 text-sm" data-testid={`message-row-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{m.client_name} <span className="font-normal text-slate-400">· {m.service_name} · {m.kind === "aviso" ? "aviso da visita de" : "ciclo"} {formatDate(m.cycle.slice(0, 10))}</span></span>
        <span className={`text-xs font-semibold ${cls}`} data-testid={`message-status-${m.id}`}>{m.channel === "email" ? "E-mail" : "WhatsApp"} · {label}</span>
      </div>
      <div className="mt-1 text-xs text-slate-400">{m.to || "sem destino"} · {m.attempts || 0} tentativa(s){m.updated_at ? ` · ${formatDate(m.updated_at.slice(0, 10))}` : ""}</div>
      {m.error && <div className="mt-1 text-xs text-rose-300" data-testid={`message-error-${m.id}`}>{m.error}</div>}
      {m.body && <p className="mt-2 line-clamp-3 rounded-lg bg-slate-950/60 p-2 text-xs text-slate-300" data-testid={`message-body-${m.id}`}>{m.edited && <b className="text-amber-300">[editada] </b>}{m.body}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {EDITABLE.includes(m.status) && (
          <Button size="sm" variant="outline" onClick={() => onEdit(m)} className="h-8 rounded-full border-slate-700 bg-transparent text-xs" data-testid={`message-edit-${m.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
        )}
      {m.status === "falhou" && (
        <Button size="sm" variant="outline" onClick={() => onRetry(m)} className="h-8 rounded-full border-slate-700 bg-transparent text-xs" data-testid={`message-retry-${m.id}`}><RotateCw className="mr-1 h-3.5 w-3.5" /> Tentar de novo</Button>
      )}
      </div>
    </li>
  );
};

export const MessagesView = ({ version, onChanged }) => {
  const [status, setStatus] = useState(ALL);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const { user } = useAuth();

  const load = useCallback((skip = 0) => {
    const params = { skip, limit: PAGE, ...(status !== ALL ? { status } : {}) };
    api.get("/messages", { params }).then((r) => { setTotal(r.data.total); setItems((prev) => (skip ? [...prev, ...r.data.items] : r.data.items)); })
      .catch((e) => toast.error(errorMessage(e)));
  }, [status]);
  useEffect(() => { load(0); }, [load, version]);

  const retry = async (m) => {
    try {
      const { data } = await api.post(`/messages/${m.id}/retry`);
      data.status === "enviada" ? toast.success("Mensagem enviada.") : toast.error(data.error || "Ainda não foi possível enviar.");
      load(0);
      onChanged();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <section className="space-y-4" data-testid="messages-view">
      <TemplatesCard onEdit={() => setSettings(true)} onWhatsApp={() => setWaOpen(true)} canEdit={user?.tenant_role === "owner"} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-lg font-bold">Histórico de mensagens <span className="font-mono text-sm text-slate-500">({total})</span></h2>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 w-full bg-slate-900 sm:w-56" data-testid="messages-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value={ALL}>Todas</SelectItem>
            {Object.entries(MESSAGE_STATUS).map(([k, [l]]) => <SelectItem key={k} value={k} data-testid={`messages-status-${k}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items.length === 0 ? <p className="rounded-xl border border-dashed border-slate-700 py-10 text-center text-sm text-slate-500" data-testid="messages-empty">Nenhuma mensagem ainda.</p> : (
        <ul className="grid gap-2 lg:grid-cols-2">{items.map((m) => <Row key={m.id} m={m} onRetry={retry} onEdit={setEditing} />)}</ul>
      )}
      {items.length < total && <Button variant="outline" onClick={() => load(items.length)} className="w-full rounded-full border-slate-700 bg-transparent" data-testid="messages-load-more">Carregar mais</Button>}
      <MessageEditDialog message={editing} onClose={() => setEditing(null)} onSaved={() => load(0)} />
      <MessagingDialog open={settings} onClose={() => setSettings(false)} onSaved={onChanged} />
      <WhatsAppSettingsDialog open={waOpen} onClose={() => setWaOpen(false)} onSaved={onChanged} />
    </section>
  );
};
